import _ from 'lodash';

import {
  DEFAULT_STICKY_TAB_ID,
  DEFAULT_TAB_ID,
  STICKY_PREFIXES,
  TAB_ID,
  TAB_PREFIX_RULES,
} from './MobileTabsProvider.constants';

/**
 * Whether a pathname is `prefix` itself or a descendant (`prefix/...`).
 *
 * @param {string} pathname
 * @param {string} prefix
 * @returns {boolean}
 */
const matchesPrefix = (pathname, prefix) =>
  pathname === prefix || pathname.startsWith(`${prefix}/`);

/**
 * Resolves the owning tab id for a pathname.
 *
 * - The home route (`/`) and any unmatched path fall back to the active/home tab.
 * - A known tab prefix wins (pipelines / home group / pricing / messages / phone).
 * - Sticky deep paths (the loan editor) stay in the tab that opened them (the active tab).
 * - Anything else gets its own pane keyed by its first path segment, so overflow ("More")
 *   destinations never pollute a bottom-nav tab's remembered position.
 *
 * @param {string} pathname
 * @param {string|null} fallbackTabId - the currently-active tab (sticky owner)
 * @returns {string}
 */
export const resolveTabId = (pathname, fallbackTabId) => {
  if (pathname === '/') {
    return TAB_ID.HOME;
  }

  const prefixRule = TAB_PREFIX_RULES.find(rule => matchesPrefix(pathname, rule.prefix));

  if (prefixRule) {
    return prefixRule.tabId;
  }

  if (STICKY_PREFIXES.some(prefix => matchesPrefix(pathname, prefix))) {
    return fallbackTabId ?? DEFAULT_STICKY_TAB_ID;
  }

  const [, firstSegment] = pathname.split('/');

  return firstSegment ? `seg:${firstSegment}` : DEFAULT_TAB_ID;
};

/**
 * Compares two locations by their navigable identity (path + query + hash), ignoring the
 * per-navigation `key` so repeated navigations to the same place are treated as no-ops.
 *
 * @param {Object} a
 * @param {Object} b
 * @returns {boolean}
 */
const isSameLocation = (a, b) =>
  !!a &&
  !!b &&
  a.pathname === b.pathname &&
  (a.search ?? '') === (b.search ?? '') &&
  (a.hash ?? '') === (b.hash ?? '');

/**
 * Inserts or updates a tab's pane with the latest location. The active tab's pane tracks the live
 * location (so forward navigation within the tab swaps screens); every other pane keeps its frozen
 * location and therefore stays mounted at its last screen. Returns the previous array unchanged when
 * nothing meaningful changed, to preserve referential stability for memoized panes.
 *
 * @param {Array<{tabId: string, location: Object}>} panes
 * @param {string} tabId
 * @param {Object} location
 * @returns {Array<{tabId: string, location: Object}>}
 */
export const upsertPane = (panes, tabId, location) => {
  const existing = panes.find(pane => pane.tabId === tabId);

  if (!existing) {
    return [...panes, { tabId, location }];
  }

  if (isSameLocation(existing.location, location)) {
    return panes;
  }

  return panes.map(pane => (pane.tabId === tabId ? { tabId, location } : pane));
};

/**
 * Root match object react-router v5's `Router` provides on context for a given pathname. Replicated
 * here because each kept-alive pane supplies its own frozen router context.
 *
 * @param {string} pathname
 * @returns {Object}
 */
export const computeRootMatch = pathname => ({
  path: '/',
  url: '/',
  params: {},
  isExact: pathname === '/',
});

/**
 * Looks up a tab's remembered location, returning null when the tab has not been visited.
 *
 * @param {Object} lastLocationByTab
 * @param {string} tabId
 * @returns {Object|null}
 */
export const getRememberedLocation = (lastLocationByTab, tabId) =>
  _.get(lastLocationByTab, tabId, null);
